/**
 * What a right-click on a draft offers.
 *
 * Open is the default - a click does the same. The rest are the things a draft
 * list is for: send it now, move or take off its time, throw it away. Sending
 * and scheduling here go through the same checks as the composer's own buttons
 * (on the server, `checkSendable`), so a draft that could not go says why
 * instead of going.
 */
import { CalendarClock, CalendarX, PenLine, Send, Trash2 } from "lucide-react";
import { useI18n, useT } from "../../../../i18n/I18nProvider";
import type { DraftListItem } from "../../../../lib/mailDrafts";
import { formatSendTime } from "./draftTime";

export function DraftItems({
  row,
  close,
  onOpen,
  onSendNow,
  onReschedule,
  onUnschedule,
  onDelete,
}: {
  row: DraftListItem;
  close: () => void;
  onOpen: (row: DraftListItem) => void;
  onSendNow: (row: DraftListItem) => void;
  /** Pick a (new) time - also how a plain draft is scheduled from the list. */
  onReschedule: (row: DraftListItem) => void;
  onUnschedule: (row: DraftListItem) => void;
  onDelete: (row: DraftListItem) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const busy = row.status === "sending";
  const act = (fn: (r: DraftListItem) => void) => {
    close();
    fn(row);
  };

  return (
    <>
      <div className="crm-menu-head">
        <div className="crm-menu-t">{row.subject || t("drafts.noSubject")}</div>
        <div className="crm-menu-s">
          {row.scheduledAt ? t("drafts.goesAt", { when: formatSendTime(row.scheduledAt, locale) }) : row.to.join(", ") || "-"}
        </div>
      </div>

      <button type="button" className="crm-mi" disabled={busy} onClick={() => act(onOpen)}>
        <PenLine className="size-4" />
        {t("drafts.open")}
      </button>

      <button type="button" className="crm-mi" disabled={busy} onClick={() => act(onSendNow)}>
        <Send className="size-4" />
        {t("drafts.sendNow")}
      </button>

      <button type="button" className="crm-mi" disabled={busy} onClick={() => act(onReschedule)}>
        <CalendarClock className="size-4" />
        {t(row.scheduledAt ? "drafts.changeTime" : "drafts.sendLater")}
      </button>

      {row.scheduledAt ? (
        <button type="button" className="crm-mi" disabled={busy} onClick={() => act(onUnschedule)}>
          <CalendarX className="size-4" />
          {t("drafts.unschedule")}
        </button>
      ) : null}

      <div className="crm-menu-sep" />
      <button type="button" className="crm-mi crm-mi-danger" disabled={busy} onClick={() => act(onDelete)}>
        <Trash2 className="size-4" />
        {t("drafts.discard")}
      </button>
    </>
  );
}
